import { useMemo } from "react";

const PAPER_DIMS = {
  A4: { w: 210, h: 297 },
  Letter: { w: 215.9, h: 279.4 },
  Half: { w: 139.7, h: 215.9 },
};

function getDims(settings) {
  const size = settings?.paper_size || "A4";
  if (size === "Custom") {
    return {
      w: Number(settings?.custom_width) || 210,
      h: Number(settings?.custom_height) || 297,
    };
  }
  return PAPER_DIMS[size] || PAPER_DIMS.A4;
}

function safe(v, fallback = "") {
  return v === undefined || v === null ? fallback : v;
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return String(iso);
  }
}

// Deterministic display code for the multi-clinic footer (same Rx → same code).
function securityCode(seed) {
  const base = String(seed || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  const src = (base + "0000000000000000000000000").slice(0, 23);
  return `${src.slice(0, 5)}-${src.slice(5, 10)}-${src.slice(10, 15)}-${src.slice(15, 20)}-${src.slice(20, 23)}`;
}

// ---- Multi-clinic template blocks (name left, clinic rows right) ----
function MultiClinicHeader({ doctor = {}, inFlow = false }) {
  const clinics = Array.isArray(doctor.clinics) ? doctor.clinics : [];
  const specialty = safe(doctor.specialty);
  return (
    <div className={inFlow ? "" : "absolute"} style={inFlow ? {} : { left: "10mm", right: "10mm", top: "8mm" }} data-testid="rx-multiclinic-header">
      <div style={{ display: "grid", gridTemplateColumns: "40% 60%", columnGap: "6mm", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontFamily: "Work Sans, Arial, sans-serif", fontWeight: 800, fontSize: "16pt", lineHeight: 1.05, color: "#1a2a7a" }}>
            {safe(doctor.name, "DOCTOR NAME, MD")}
          </div>
          {specialty ? (
            <div style={{ fontSize: "9pt", marginTop: "1mm", color: "#0e7c7b", fontWeight: 600 }}>{specialty}</div>
          ) : null}
        </div>
        <div style={{ fontSize: "7pt", lineHeight: 1.3, color: "#0f172a" }}>
          {clinics.map((c, i) => (
            <div key={i} style={{ padding: "0.4mm 0", borderBottom: i < clinics.length - 1 ? "0.3pt dotted #cbd5e1" : "none" }}>
              <b>{safe(c.name)}{c.name ? ":" : ""}</b> {safe(c.schedule)}{c.contact ? ` CP: ${c.contact}` : ""}
            </div>
          ))}
        </div>
      </div>
      <div style={{ borderBottom: "2px solid #0f172a", marginTop: "2mm" }} />
    </div>
  );
}

function MultiClinicPatient({ patient = {}, prescription = {} }) {
  const line = { flex: 1, borderBottom: "0.5pt solid #94a3b8", textAlign: "center", fontWeight: 600, paddingBottom: "0.3mm" };
  return (
    <div className="absolute" style={{ left: "10mm", right: "10mm", top: "42mm", fontSize: "10pt", color: "#0f172a" }} data-testid="rx-multiclinic-patient">
      <div style={{ display: "flex", alignItems: "flex-end", gap: "3mm", marginBottom: "3.5mm" }}>
        <span>Name</span><span style={line} data-testid="rx-patient-name">{safe(patient.full_name)}</span>
        <span style={{ marginLeft: "4mm" }}>Date</span><span style={{ ...line, flex: "none", width: "42mm" }} data-testid="rx-date">{formatDate(prescription?.date)}</span>
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: "3mm" }}>
        <span>Address</span><span style={line} data-testid="rx-patient-address">{safe(patient.address)}</span>
        <span style={{ marginLeft: "4mm" }}>Age</span><span style={{ ...line, flex: "none", width: "18mm" }} data-testid="rx-patient-age">{safe(patient.age, "")}</span>
        <span>Sex</span><span style={{ ...line, flex: "none", width: "20mm" }} data-testid="rx-patient-gender">{safe(patient.gender, "")}</span>
      </div>
      <div style={{ borderBottom: "2px solid #0f172a", marginTop: "3mm" }} />
    </div>
  );
}

function MultiClinicFooter({ doctor = {}, prescription = {}, followUpDate = "" }) {
  // Follow-up comes from the Follow-up module (the patient's scheduled next visit),
  // passed in live; the prescription's own date and frozen snapshot are fallbacks.
  const _fu = prescription?.follow_up_date || followUpDate || prescription?.patient_snapshot?.next_visit_date;
  const followUp = _fu ? formatDate(_fu) : "";
  const showFollow = doctor.follow_up_enabled !== false;
  const showSecurity = doctor.security_code_enabled !== false;
  const footerText = safe(doctor.affiliations_footer);
  return (
    <div className="absolute" style={{ left: "10mm", right: "10mm", bottom: "6mm", color: "#0f172a" }} data-testid="rx-multiclinic-footer">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        {showFollow ? (
          <div style={{ fontSize: "10pt" }}>Follow Up: <span style={{ display: "inline-block", width: "45mm", borderBottom: "0.5pt solid #0f172a" }}>{followUp}</span></div>
        ) : <div />}
        <div style={{ textAlign: "right" }}>
          {doctor.signature_image_url ? (
            <img src={doctor.signature_image_url} alt="signature" className="rx-signature" style={{ maxHeight: "12mm", marginBottom: "-2px" }} />
          ) : null}
          <div style={{ fontWeight: 800, textDecoration: "underline", fontSize: "10pt" }}>{safe(doctor.name, "DOCTOR NAME, MD")}</div>
          <div style={{ fontSize: "9pt" }}>PRC Number: {safe(doctor.license_number) || "_________"}</div>
          <div style={{ fontSize: "9pt" }}>PTR Number: {safe(doctor.ptr_number) || "_________"}</div>
        </div>
      </div>
      {footerText.trim() ? (
        <div style={{ marginTop: "2mm", fontSize: "6.5pt", lineHeight: 1.35 }}><b>HOSPITAL AFFILIATIONS:</b> {footerText}</div>
      ) : null}
      {showSecurity ? (
        <div style={{ marginTop: "1.5mm", textAlign: "center", fontSize: "7pt", color: "#334155" }}>
          Security Code: {securityCode(prescription?.id || prescription?.prescription_number)}
        </div>
      ) : null}
    </div>
  );
}

// Shared doctor header so every page (Rx, lab request, request groups) renders
// the SAME letterhead — classic affiliations grid or the multi-clinic layout.
export function RxDocHeader({ s = {}, doctor = {}, affiliations = [], isMulti = false, inFlow = false }) {
  if (isMulti) return <MultiClinicHeader doctor={doctor} inFlow={inFlow} />;
  if (!(s.show_doctor_header !== false && !s.template_image_url)) return null;
  return (
    <div className={inFlow ? "" : "absolute"} style={inFlow ? {} : { left: "10mm", right: "10mm", top: "8mm" }}>
      <div className="text-center">
        <div className="font-bold text-slate-900" style={{ fontFamily: "Work Sans, Arial, sans-serif", fontSize: "15pt", letterSpacing: "0.01em", lineHeight: 1.08 }}>
          {safe(doctor.name, s.doctor_name) || "DOCTOR NAME, MD"}
        </div>
        {(safe(doctor.specialty, s.specialty) || s.specialty) ? (
          <div className="text-slate-800 mt-0.5" style={{ fontFamily: "Arial, sans-serif", fontSize: "9pt", lineHeight: 1.15 }}>
            {safe(doctor.specialty, s.specialty)}
          </div>
        ) : null}
      </div>

      {Array.isArray(affiliations) && affiliations.length > 0 ? (
        <div
          className="mt-2 text-[7pt] leading-[1.12] text-slate-900"
          style={{ fontFamily: "Arial, sans-serif", display: "grid", gridTemplateColumns: "1.05fr 0.95fr 1.1fr", columnGap: "8mm" }}
        >
          {affiliations.map((af, ai) => (
            <div key={ai}>
              {ai === 0 ? <div className="italic underline mb-0.5">Affiliations:</div> : null}
              <div className="font-bold uppercase">{af.name}</div>
              {(af.lines || []).map((ln, li) => {
                const isHospitalLine = /^[A-Z][A-Z .,&]+$/.test(ln) && ln.length > 6 && ln === ln.toUpperCase();
                return (
                  <div key={li} className={isHospitalLine ? "font-bold uppercase" : ""}>{ln}</div>
                );
              })}
            </div>
          ))}
        </div>
      ) : null}

      <div className="border-b-2 border-slate-900 mt-2" />
    </div>
  );
}

export default function PrescriptionPreview({ prescription, settings, scale = 1, referral = null, followUpDate = "" }) {
  const s = settings || {};
  const doctor = prescription?.doctor_snapshot || {};
  const template = doctor.layout_template || s.layout_template || "classic";
  const isMulti = template === "multi_clinic";

  const affiliations = Array.isArray(doctor.affiliations) && doctor.affiliations.length > 0
    ? doctor.affiliations
    : (Array.isArray(s.affiliations) ? s.affiliations : []);

  const headerImageUrl = "";
  const headerImageHeightValue = 0;
  const dims = getDims(s);
  const meds = Array.isArray(prescription?.medicines) ? prescription.medicines : [];
  // Doctor RX profile (per-doctor) overrides hospital print_settings for medicine table formatting.
  const docMedFont = Number(doctor.medicine_font_size) || 0;
  const docInsFont = Number(doctor.instruction_font_size) || 0;
  const docRowsPerPage = Number(doctor.rows_per_page) || 0;
  const docRowHeight = Number(doctor.row_height) || 0;
  // Safety cap: RX page must never exceed 5 medicine items.
  // Doctor profile may reduce rows_per_page, but cannot exceed 5.
  const configuredRowsPerPage = docRowsPerPage || Number(s.max_rows_per_page) || 5;
  const maxRows = Math.max(1, Math.min(Number(configuredRowsPerPage) || 5, 5));
  const rowHeight = docRowHeight || Number(s.row_height) || 8;
  // Watermark follows the doctor: when they've set their own, it overrides the
  // hospital's so it prints the same at every affiliated hospital.
  const wm = doctor.use_own_watermark ? {
    show_watermark: doctor.show_watermark,
    watermark_type: doctor.watermark_type,
    watermark_text: doctor.watermark_text,
    watermark_image_url: doctor.watermark_image_url,
    watermark_opacity: doctor.watermark_opacity,
    watermark_image_size: doctor.watermark_image_size,
    watermark_rotation: doctor.watermark_rotation,
    watermark_font_size: doctor.watermark_font_size,
  } : s;
  const fontSize = Number(s.font_size) || 12;
  const bodyFontSize = docMedFont || Number(s.body_font_size) || fontSize || 12;
  const medicineFontSize = docMedFont || Number(s.body_font_size) || 11;
  const instructionFontSize = docInsFont || 10;
  const autoFit = s.auto_fit !== false;

  const hasBeltranHeader = !s.template_image_url && !headerImageUrl && affiliations.length > 0;
  const headerImgHeight = headerImageUrl ? headerImageHeightValue : 0;
  const headerOffset = headerImgHeight ? headerImgHeight + 6 : 0;

  const patientY = Math.max(Number(s.patient_y) || 50, hasBeltranHeader ? 52 : 0, headerOffset);
  const bodyStartY = Math.max(Number(s.body_start_y) || 80, hasBeltranHeader ? patientY + 16 : 0, headerOffset + 16);
  const signatureY = Math.min(Math.max(Number(s.signature_y) || 235, hasBeltranHeader ? 215 : 0), dims.h - 32);

  const pages = useMemo(() => {
    if (meds.length === 0) return [[]];
    const out = [];
    for (let i = 0; i < meds.length; i += maxRows) {
      out.push(meds.slice(i, i + maxRows));
    }
    return out;
  }, [meds, maxRows]);

  const sigMode = s.signature_mode || "every_page";
  const patient = prescription?.patient_snapshot || {};

  const paperSizeClass = s.paper_size === "Letter" ? "size-Letter"
    : s.paper_size === "Half" ? "size-Half"
    : s.paper_size === "Custom" ? "" : "size-A4";

  const pageStyle = s.paper_size === "Custom" ? { width: `${dims.w}mm`, minHeight: `${dims.h}mm` } : {};

  const patientInfoStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    textAlign: "left",
    lineHeight: 1.45,
  };

  const patientRowStyle = {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "flex-start",
    textAlign: "left",
    width: "100%",
    gap: "4mm",
  };

  const patientLabelStyle = {
    minWidth: "18mm",
    color: "#475569",
    textAlign: "left",
    whiteSpace: "nowrap",
  };

  const patientValueStyle = {
    fontWeight: 500,
    textAlign: "left",
    textDecoration: "none",
    borderBottom: "none",
    whiteSpace: "normal",
    wordBreak: "break-word",
  };

  return (
    <div className="print-area" data-testid="prescription-preview">
      <div style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}>
        {pages.map((pageMeds, pi) => {
          const isLast = pi === pages.length - 1;
          const showSignature = sigMode === "every_page" || isLast;

          return (
            <div
              key={pi}
              className={`rx-paper ${paperSizeClass} ${autoFit ? "auto-fit" : ""}`}
              style={pageStyle}
              data-testid={`rx-page-${pi + 1}`}
              data-print-label={pages.length > 1 ? `Prescription (${pi + 1} of ${pages.length})` : "Prescription"}
            >
              {s.template_image_url ? (
                <div
                  className="rx-template-bg"
                  style={{ backgroundImage: `url(${s.template_image_url})` }}
                />
              ) : null}

              <div className="rx-content" style={{ fontSize: `${fontSize}pt`, position: "relative", minHeight: `${dims.h}mm` }}>
              {wm.show_watermark !== false ? (
                <div
                  className="absolute pointer-events-none select-none overflow-hidden"
                  style={{
                    zIndex: 1,
                    left: 0,
                    top: 0,
                    width: "100%",
                    height: "100%",
                    minHeight: "297mm",
                  }}
                  data-testid="rx-watermark"
                >
                  {wm.watermark_type === "image" && wm.watermark_image_url ? (
                    <img
                      src={wm.watermark_image_url}
                      alt=""
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        width: `${Number(wm.watermark_image_size ?? 55)}%`,
                        maxWidth: "90%",
                        height: "auto",
                        opacity: Number(wm.watermark_opacity ?? 0.10),

                        /* Perfect center */
                        transform: "translate(-50%, -50%)",

                        /* Keep behind content */
                        zIndex: 0,

                        /* Prevent stretching */
                        objectFit: "contain",

                        /* Better visual centering on A4 */
                        transformOrigin: "center center",

                        /* Slight upward adjustment to compensate
                           for the prescription layout occupying
                           the upper portion of the page */
                        marginTop: "-18mm",
                      }}
                    />
                  ) : (
                    Array.from({ length: 30 }).map((_, i) => {
                      const col = i % 5;
                      const row = Math.floor(i / 5);

                      return (
                        <div
                          key={i}
                          style={{
                            position: "absolute",
                            left: `${col * 22 + 2}%`,
                            top: `${row * 16 + 3}%`,
                            opacity: Number(wm.watermark_opacity ?? 0.10),
                            transform: `rotate(${Number(wm.watermark_rotation ?? -35)}deg)`,
                            fontSize: `${Number(wm.watermark_font_size ?? 28)}pt`,
                            fontWeight: 700,
                            color: "#334155",
                            whiteSpace: "nowrap",
                            fontFamily: "Work Sans, Arial, sans-serif",
                          }}
                        >
                          {String(wm.watermark_text || "Rx")}
                        </div>
                      );
                    })
                  )}
                </div>
              ) : null}
                {headerImageUrl ? (
                  <div
                    className="absolute"
                    style={{ left: 0, right: 0, top: 0, height: `${headerImgHeight}mm` }}
                    data-testid="rx-header-image"
                  >
                    <img
                      src={headerImageUrl}
                      alt="header"
                      style={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "center top" }}
                    />
                  </div>
                ) : null}

                {isMulti ? (
                  <MultiClinicHeader doctor={doctor} />
                ) : s.show_doctor_header !== false && !s.template_image_url && !headerImageUrl ? (
                  <div
                    className="absolute"
                    style={{ left: "10mm", right: "10mm", top: "8mm" }}
                  >
                    <div className="text-center">
                      <div className="font-bold text-slate-900" style={{ fontFamily: "Work Sans, Arial, sans-serif", fontSize: "15pt", letterSpacing: "0.01em", lineHeight: 1.08 }}>
                        {safe(doctor.name, s.doctor_name) || "DOCTOR NAME, MD"}
                      </div>
                      {(safe(doctor.specialty, s.specialty) || s.specialty) ? (
                        <div className="text-slate-800 mt-0.5" style={{ fontFamily: "Arial, sans-serif", fontSize: "9pt", lineHeight: 1.15 }}>
                          {safe(doctor.specialty, s.specialty)}
                        </div>
                      ) : null}
                    </div>

                    {Array.isArray(affiliations) && affiliations.length > 0 ? (
                      <div
                        className="mt-2 text-[7pt] leading-[1.12] text-slate-900"
                        style={{
                          fontFamily: "Arial, sans-serif",
                          display: "grid",
                          gridTemplateColumns: "1.05fr 0.95fr 1.1fr",
                          columnGap: "8mm",
                        }}
                      >
                        {affiliations.map((af, ai) => (
                          <div key={ai}>
                            {ai === 0 ? <div className="italic underline mb-0.5">Affiliations:</div> : null}
                            <div className="font-bold uppercase">{af.name}</div>
                            {(af.lines || []).map((ln, li) => {
                              const isHospitalLine = /^[A-Z][A-Z .,&]+$/.test(ln) && ln.length > 6 && ln === ln.toUpperCase();
                              return (
                                <div key={li} className={isHospitalLine ? "font-bold uppercase" : ""}>
                                  {ln}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    ) : null}

                    <div className="border-b-2 border-slate-900 mt-2" />
                  </div>
                ) : null}

                {isMulti ? (
                  <MultiClinicPatient patient={patient} prescription={prescription} />
                ) : (
                <div
                  className="absolute"
                  style={{
                    left: "10mm",
                    right: "10mm",
                    top: `${patientY}mm`,
                    ...patientInfoStyle,
                  }}
                  data-testid="rx-patient-block"
                >
                  <div style={patientRowStyle}>
                    <span style={patientLabelStyle}>Patient:</span>
                    <span style={patientValueStyle} data-testid="rx-patient-name">
                      {safe(patient.full_name) || ""}
                    </span>
                  </div>

                  <div style={patientRowStyle}>
                    <span style={patientLabelStyle}>Address:</span>
                    <span style={patientValueStyle} data-testid="rx-patient-address">
                      {safe(patient.address) || ""}
                    </span>
                  </div>

                  <div style={{ ...patientRowStyle, gap: "10mm" }}>
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "flex-start", textAlign: "left" }}>
                      <span style={{ ...patientLabelStyle, minWidth: "10mm" }}>Age:</span>
                      <span style={patientValueStyle} data-testid="rx-patient-age">
                        {safe(patient.age, "")}
                      </span>
                    </div>

                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "flex-start", textAlign: "left" }}>
                      <span style={{ ...patientLabelStyle, minWidth: "16mm" }}>Gender:</span>
                      <span style={patientValueStyle} data-testid="rx-patient-gender">
                        {safe(patient.gender, "")}
                      </span>
                    </div>

                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "flex-start", textAlign: "left" }}>
                      <span style={{ ...patientLabelStyle, minWidth: "12mm" }}>Date:</span>
                      <span style={patientValueStyle} data-testid="rx-date">
                        {formatDate(prescription?.date)}
                      </span>
                    </div>
                  </div>
                </div>
                )}

                <div
                  className="absolute"
                  style={{
                    left: `${Number(s.left_margin) || 20}mm`,
                    top: `${isMulti ? 66 : bodyStartY}mm`,
                    right: `${Math.max(Number(s.left_margin) || 20, 28)}mm`,
                    fontSize: `${bodyFontSize}pt`,
                  }}
                >
                  <div className="font-serif italic text-slate-900 mb-2 leading-none" style={{ fontSize: "42pt" }}>
                    R<sub style={{ fontSize: "28pt", marginLeft: "-6px" }}>x</sub>
                  </div>

                  {pi === 0 && prescription?.diagnosis ? (
                    <div className="mb-3 border-l-4 border-blue-600 pl-2 py-0.5" data-testid="rx-diagnosis">
                      <div className="text-[9pt] uppercase tracking-wider text-slate-500 font-semibold">Diagnosis</div>
                      <div className="text-slate-900 whitespace-pre-line">{prescription.diagnosis}</div>
                    </div>
                  ) : null}

                  <div className="space-y-1">
                    {pageMeds.length === 0 ? (
                      prescription?.diagnosis ? null : (
                        <div className="text-slate-400 italic text-sm">No medicines — diagnosis / referral only.</div>
                      )
                    ) : (
                      pageMeds.map((m, i) => {
                        const qty = m.total_quantity;
                        const showQty = !m.is_supply && qty != null && qty !== "" && Number(qty) > 0;

                        return (
                          <div
                            key={i}
                            className="rx-row flex items-center gap-3 border-b border-dashed border-slate-200 pb-1"
                            style={{ minHeight: `${rowHeight}mm` }}
                            data-testid={`rx-row-${pi}-${i}`}
                          >
                            <span className="text-slate-500 w-6 text-right self-start pt-0.5">{pi * maxRows + i + 1}.</span>
                            <div className="flex-1 min-w-0">
                              <div
                                className="font-bold text-slate-900"
                                style={{ fontSize: `${medicineFontSize}pt`, lineHeight: 1.15 }}
                              >
                                {safe(m.medicine_name)}
                              </div>
                              <div className="text-slate-600 mt-0.5" style={{ fontSize: `${instructionFontSize}pt`, fontWeight: "400", lineHeight: 1.2 }}>
                                {m.dosage ? <span className="mr-3"><b>Sig:</b> {safe(m.dosage)}</span> : null}
                                {m.frequency ? <span className="mr-3">{safe(m.frequency)}</span> : null}
                                {m.duration ? <span className="mr-3">× {safe(m.duration)}</span> : null}
                                {m.instruction ? <span className="italic">— {m.instruction}</span> : null}
                              </div>
                            </div>

                            {showQty ? (
                              <div
                                className="flex items-center justify-end text-slate-700 font-semibold"
                                data-testid={`rx-qty-${pi}-${i}`}
                                style={{
                                  minWidth: "8mm",
                                  fontSize: `${medicineFontSize}pt`,
                                  fontFamily: "Work Sans, Arial, sans-serif",
                                }}
                              >
                                {qty}
                              </div>
                            ) : m.is_supply ? (
                              <div className="text-[9pt] text-slate-500 italic" style={{ minWidth: "22mm" }} data-testid={`rx-supply-${pi}-${i}`}>
                                supply{qty ? ` × ${qty}` : ""}
                              </div>
                            ) : null}
                          </div>
                        );
                      })
                    )}
                  </div>

                  {isLast && prescription?.general_instruction ? (
                    <div className="mt-4 text-xs text-slate-700 border-t border-slate-200 pt-2">
                      <b>Notes:</b> {prescription.general_instruction}
                    </div>
                  ) : null}
                </div>

                {isMulti ? (
                  isLast ? <MultiClinicFooter doctor={doctor} prescription={prescription} followUpDate={followUpDate} /> : null
                ) : showSignature ? (
                  <>
                    <div
                      className="absolute"
                      style={{
                        left: `${Number(s.left_margin) || 20}mm`,
                        top: `${signatureY}mm`,
                        width: "85mm",
                      }}
                    >
                      <div className="text-sm">
                        <span className="text-slate-700">Follow-up on </span>
                        <span className="underline">{(() => { const fu = prescription?.follow_up_date || followUpDate || prescription?.patient_snapshot?.next_visit_date; return fu ? formatDate(fu) : "_____________________"; })()}</span>
                      </div>
                    </div>

                    <div
                      className="absolute text-left"
                      style={{
                        right: "10mm",
                        top: `${signatureY - 8}mm`,
                        width: "58mm",
                        maxWidth: "58mm",
                        overflow: "hidden",
                        fontSize: "8pt",
                        transform: "scale(0.9)",
                        transformOrigin: "top right",
                      }}
                    >
                      {s.signature_image_url ? (
                        <img
                          src={s.signature_image_url}
                          alt="signature"
                          className="rx-signature"
                          style={{ maxHeight: "14mm", marginBottom: "-6px" }}
                        />
                      ) : (
                        <div style={{ height: "10mm" }} />
                      )}
                      <div
                        className="font-bold uppercase leading-tight"
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          width: "100%",
                          fontSize: "8pt",
                        }}
                      >
                        {safe(doctor.name, s.doctor_name) || "DOCTOR NAME, MD"}
                      </div>
                      <div className="text-[8pt] mt-0.5">
                        Lic. No. : <span className="underline">{safe(doctor.license_number) || safe(prescription?.doctor_snapshot?.license_number) || safe(s.license_number) || "_____________"}</span>
                      </div>
                      <div className="text-[8pt]">
                        PTR No. : <span className="underline">{safe(doctor.ptr_number) || safe(prescription?.doctor_snapshot?.ptr_number) || safe(s.ptr_number) || "_____________"}</span>
                      </div>
                      {doctor.s2_number ? (
                        <div className="text-[8pt]">
                          S2 No. : <span className="underline">{safe(doctor.s2_number)}</span>
                        </div>
                      ) : null}
                    </div>
                  </>
                ) : null}

                {!isMulti && isLast && Array.isArray(s.footer_medicines) && s.footer_medicines.length > 0 && !s.template_image_url ? (
                  <div
                    className="absolute"
                    style={{
                      left: "10mm",
                      right: "10mm",
                      bottom: "12mm",
                    }}
                  >
                    <div className="grid grid-cols-3 gap-x-3 gap-y-2">
                      {s.footer_medicines.map((fm, fi) => (
                        <div key={fi} className="text-center">
                          <div className="border border-slate-800 px-2 py-0.5 text-[8.5pt] font-bold uppercase tracking-wide">
                            {fm.name}
                          </div>
                          <div className="text-[8pt] text-slate-700 mt-0.5">{fm.subtitle}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {pages.length > 1 && !(isMulti && isLast) ? (
                <div
                  className="absolute text-[7pt] text-slate-400"
                  style={{ left: "8mm", bottom: "4mm" }}
                >
                  Rx# {safe(prescription?.prescription_number, "DRAFT")} · Page {pi + 1} of {pages.length}
                </div>
                ) : null}
              </div>
            </div>
          );
        })}

        {Array.isArray(prescription?.lab_attachments) && prescription.lab_attachments.map((att, ai) => (
          <div
            key={`lab-${att.id || ai}`}
            className={`rx-paper ${paperSizeClass} ${autoFit ? "auto-fit" : ""}`}
            style={pageStyle}
            data-testid={`rx-lab-page-${ai}`}
            data-print-label={att.title || "Laboratory Result"}
          >
            {s.template_image_url ? (
              <div className="rx-template-bg" style={{ backgroundImage: `url(${s.template_image_url})` }} />
            ) : null}

            <div className="rx-content" style={{ fontSize: `${fontSize}pt`, position: "relative", minHeight: `${dims.h}mm` }}>
              {wm.show_watermark !== false ? (
                <div
                  className="absolute pointer-events-none select-none overflow-hidden"
                  style={{
                    zIndex: 1,
                    left: 0,
                    top: 0,
                    width: "100%",
                    height: "100%",
                    minHeight: "297mm",
                  }}
                  data-testid="rx-watermark"
                >
                  {wm.watermark_type === "image" && wm.watermark_image_url ? (
                    <img
                      src={wm.watermark_image_url}
                      alt=""
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        width: `${Number(wm.watermark_image_size ?? 55)}%`,
                        maxWidth: "90%",
                        height: "auto",
                        opacity: Number(wm.watermark_opacity ?? 0.10),

                        /* Perfect center */
                        transform: "translate(-50%, -50%)",

                        /* Keep behind content */
                        zIndex: 0,

                        /* Prevent stretching */
                        objectFit: "contain",

                        /* Better visual centering on A4 */
                        transformOrigin: "center center",

                        /* Slight upward adjustment to compensate
                           for the prescription layout occupying
                           the upper portion of the page */
                        marginTop: "-18mm",
                      }}
                    />
                  ) : (
                    Array.from({ length: 30 }).map((_, i) => {
                      const col = i % 5;
                      const row = Math.floor(i / 5);

                      return (
                        <div
                          key={i}
                          style={{
                            position: "absolute",
                            left: `${col * 22 + 2}%`,
                            top: `${row * 16 + 3}%`,
                            opacity: Number(wm.watermark_opacity ?? 0.10),
                            transform: `rotate(${Number(wm.watermark_rotation ?? -35)}deg)`,
                            fontSize: `${Number(wm.watermark_font_size ?? 28)}pt`,
                            fontWeight: 700,
                            color: "#334155",
                            whiteSpace: "nowrap",
                            fontFamily: "Work Sans, Arial, sans-serif",
                          }}
                        >
                          {String(wm.watermark_text || "Rx")}
                        </div>
                      );
                    })
                  )}
                </div>
              ) : null}
              {headerImageUrl ? (
                <div
                  className="absolute"
                  style={{ left: 0, right: 0, top: 0, height: `${headerImgHeight}mm` }}
                  data-testid="rx-header-image"
                >
                  <img
                    src={headerImageUrl}
                    alt="header"
                    style={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "center top" }}
                  />
                </div>
              ) : null}

              <RxDocHeader s={s} doctor={doctor} affiliations={affiliations} isMulti={isMulti} />

              <div
                className="absolute"
                style={{
                  left: "12mm",
                  right: "12mm",
                  top: `${Math.max(52, headerOffset)}mm`,
                  fontSize: "10pt",
                  ...patientInfoStyle,
                }}
              >
                <div style={patientRowStyle}>
                  <span style={patientLabelStyle}>Patient:</span>
                  <span style={patientValueStyle}>
                    {safe(prescription?.patient_snapshot?.full_name) || "—"}
                  </span>
                </div>

                <div style={patientRowStyle}>
                  <span style={patientLabelStyle}>Date:</span>
                  <span style={patientValueStyle}>
                    {formatDate(prescription?.date)}
                  </span>
                </div>
              </div>

              <div className="absolute text-center" style={{ left: 0, right: 0, top: `${Math.max(66, headerOffset + 12)}mm` }}>
                <div className="inline-block border-2 border-slate-900 px-5 py-1 uppercase font-bold tracking-[0.25em]" style={{ fontFamily: "Work Sans, Arial, sans-serif", fontSize: "12pt" }}>
                  Laboratory Attachment
                </div>
                {att.title ? (
                  <div className="mt-2 text-[10pt] text-slate-700">{att.title}</div>
                ) : null}
                <div className="mt-1 text-[8pt] text-slate-500 mono">{att.filename}</div>
              </div>

              <div
                className="absolute flex items-center justify-center"
                style={{ left: "10mm", right: "10mm", top: `${Math.max(82, headerOffset + 30)}mm`, bottom: "20mm" }}
              >
                <img
                  src={att.data_url}
                  alt={att.title || att.filename || "lab attachment"}
                  style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                />
              </div>

              <div className="absolute text-[7pt] text-slate-400" style={{ left: "8mm", bottom: "4mm" }}>
                Rx# {safe(prescription?.prescription_number, "DRAFT")} · Lab attachment {ai + 1} of {prescription.lab_attachments.length}
              </div>
            </div>
          </div>
        ))}

        {Array.isArray(prescription?.request_groups) && prescription.request_groups.map((group, gi) => {
          const items = Array.isArray(group.items) ? group.items : String(group.items || "").split(/\r?\n/).filter(Boolean);
          const title = group.title || "Request";
          if (!items.length && !String(group.notes || "").trim()) return null;

          return (
            <div
              key={`request-group-${gi}`}
              className={`rx-paper allow-break ${paperSizeClass} ${autoFit ? "auto-fit" : ""}`}
              style={{ ...pageStyle, minHeight: `${dims.h}mm` }}
              data-testid={`rx-request-group-page-${gi}`}
              data-print-label={title}
            >
              {s.template_image_url ? (
                <div className="rx-template-bg" style={{ backgroundImage: `url(${s.template_image_url})` }} />
              ) : null}

              {/* Table so the letterhead (thead) repeats on every printed page. */}
              <table className="rx-content" style={{ width: "100%", borderCollapse: "collapse", fontSize: `${fontSize}pt` }}>
                <thead className="rx-repeat-header">
                  <tr><td style={{ padding: "12mm 10mm 0 10mm" }}>
                    <RxDocHeader s={s} doctor={doctor} affiliations={affiliations} isMulti={isMulti} inFlow />
                    {/* Patient block lives INSIDE the repeating <thead>. As the first
                        row of <tbody> under a table-header-group it gets dropped on
                        print (these request pages are allow-break, so Chrome paginates
                        them) — that is the "patient details show in preview but vanish
                        when printed" bug. Header-group content prints reliably, and
                        this also puts the patient on every page of a long request. */}
                    <div style={{ ...patientInfoStyle, marginTop: "4mm", fontSize: `${bodyFontSize}pt` }}>
                      <div style={patientRowStyle}>
                        <span style={patientLabelStyle}>Patient:</span>
                        <span style={patientValueStyle}>{safe(prescription?.patient_snapshot?.full_name) || ""}</span>
                      </div>
                      <div style={patientRowStyle}>
                        <span style={patientLabelStyle}>Address:</span>
                        <span style={patientValueStyle}>{safe(prescription?.patient_snapshot?.address) || ""}</span>
                      </div>
                      <div style={{ ...patientRowStyle, gap: "10mm" }}>
                        <div style={{ display: "flex", alignItems: "baseline" }}>
                          <span style={{ ...patientLabelStyle, minWidth: "10mm" }}>Age:</span>
                          <span style={patientValueStyle}>{safe(prescription?.patient_snapshot?.age, "—")}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "baseline" }}>
                          <span style={{ ...patientLabelStyle, minWidth: "16mm" }}>Gender:</span>
                          <span style={patientValueStyle}>{safe(prescription?.patient_snapshot?.gender, "—")}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "baseline" }}>
                          <span style={{ ...patientLabelStyle, minWidth: "12mm" }}>Date:</span>
                          <span style={patientValueStyle}>{formatDate(prescription?.date)}</span>
                        </div>
                      </div>
                    </div>
                  </td></tr>
                </thead>
                <tbody>
                  <tr><td style={{ padding: "3mm 10mm 12mm 10mm", verticalAlign: "top", fontSize: `${bodyFontSize}pt` }}>

                    <div className="text-center mb-4 print-keep">
                      <div className="inline-block border-2 border-slate-900 px-6 py-1.5 uppercase font-bold tracking-[0.25em]" style={{ fontFamily: "Work Sans, Arial, sans-serif", fontSize: "14pt" }}>
                        {title}
                      </div>
                    </div>

                    {prescription?.diagnosis ? (
                      <div className="mb-4 text-sm">
                        <span className="font-semibold">Diagnosis / Impression:</span> {prescription.diagnosis}
                      </div>
                    ) : null}

                    <div className="space-y-2 pl-[22mm]" data-testid={`rx-request-group-body-${gi}`}>
                      {items.map((item, ii) => (
                        <div key={ii} className="flex gap-2">
                          <span className="font-bold">•</span>
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>

                    {group.notes ? (
                      <div className="mt-5 pl-[22mm] whitespace-pre-wrap">
                        <div className="font-semibold mb-1">Notes / Purpose:</div>
                        {group.notes}
                      </div>
                    ) : null}

                    <div className="print-keep" style={{ marginTop: "16mm", display: "flex", justifyContent: "flex-end" }}>
                      <div className="text-left" style={{ width: "58mm", fontSize: "8pt" }}>
                        {(doctor.signature_image_url || s.signature_image_url) ? (
                          <img src={doctor.signature_image_url || s.signature_image_url} alt="signature" className="rx-signature" style={{ maxHeight: "14mm", marginBottom: "-6px" }} />
                        ) : (<div style={{ height: "10mm" }} />)}
                        <div className="font-bold uppercase leading-tight" style={{ fontSize: "8pt" }}>
                          {safe(doctor.name, s.doctor_name) || "DOCTOR NAME, MD"}
                        </div>
                        <div className="text-slate-700 leading-tight" style={{ fontSize: "7pt" }}>{safe(doctor.specialty, s.specialty)}</div>
                        <div className="text-slate-700 leading-tight" style={{ fontSize: "7pt" }}>Lic. No. {safe(doctor.license_number, s.license_number) || "—"}</div>
                        <div className="text-slate-700 leading-tight" style={{ fontSize: "7pt" }}>PTR No. {safe(doctor.ptr_number, s.ptr_number) || "—"}</div>
                      </div>
                    </div>

                    <div className="text-[7pt] text-slate-400 mt-4">
                      Rx# {safe(prescription?.prescription_number, "DRAFT")} · {title}
                    </div>

                  </td></tr>
                </tbody>
              </table>
            </div>
          );
        })}

        {(!Array.isArray(prescription?.request_groups) || prescription.request_groups.length === 0) && prescription?.request_text ? (
          <div
            className={`rx-paper allow-break ${paperSizeClass} ${autoFit ? "auto-fit" : ""}`}
            style={{ ...pageStyle, minHeight: `${dims.h}mm`, position: "relative" }}
            data-testid="rx-request-page"
            data-print-label={prescription?.request_title || "Request"}
          >
            {s.template_image_url ? (
              <div className="rx-template-bg" style={{ backgroundImage: `url(${s.template_image_url})` }} />
            ) : null}

            {wm.show_watermark !== false ? (
              <div
                className="absolute pointer-events-none select-none overflow-hidden"
                style={{ zIndex: 0, left: 0, top: 0, width: "100%", height: "100%", minHeight: "297mm" }}
                data-testid="rx-watermark"
              >
                {wm.watermark_type === "image" && wm.watermark_image_url ? (
                  <img
                    src={wm.watermark_image_url}
                    alt=""
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: "50%",
                      top: "50%",
                      width: `${Number(wm.watermark_image_size ?? 55)}%`,
                      maxWidth: "90%",
                      height: "auto",
                      opacity: Number(wm.watermark_opacity ?? 0.10),
                      transform: "translate(-50%, -50%)",
                      zIndex: 0,
                      objectFit: "contain",
                      transformOrigin: "center center",
                      marginTop: "-18mm",
                    }}
                  />
                ) : (
                  Array.from({ length: 30 }).map((_, i) => {
                    const col = i % 5;
                    const row = Math.floor(i / 5);
                    return (
                      <div
                        key={i}
                        style={{
                          position: "absolute",
                          left: `${col * 22 + 2}%`,
                          top: `${row * 16 + 3}%`,
                          opacity: Number(wm.watermark_opacity ?? 0.10),
                          transform: `rotate(${Number(wm.watermark_rotation ?? -35)}deg)`,
                          fontSize: `${Number(wm.watermark_font_size ?? 28)}pt`,
                          fontWeight: 700,
                          color: "#334155",
                          whiteSpace: "nowrap",
                          fontFamily: "Work Sans, Arial, sans-serif",
                        }}
                      >
                        {String(wm.watermark_text || "Rx")}
                      </div>
                    );
                  })
                )}
              </div>
            ) : null}

            {/* Table so the letterhead (thead) repeats on every printed page. */}
            <table className="rx-content" style={{ width: "100%", borderCollapse: "collapse", position: "relative", zIndex: 1, fontSize: `${fontSize}pt` }}>
              <thead className="rx-repeat-header">
                <tr><td style={{ padding: "12mm 10mm 0 10mm" }}>
                  <RxDocHeader s={s} doctor={doctor} affiliations={affiliations} isMulti={isMulti} inFlow />
                  {/* Patient block lives INSIDE the repeating <thead> — see the
                      request-group page above for why (a table-header-group drops the
                      first <tbody> block on print for allow-break pages). */}
                  <div style={{ ...patientInfoStyle, marginTop: "4mm", fontSize: `${bodyFontSize}pt` }}>
                    <div style={patientRowStyle}>
                      <span style={patientLabelStyle}>Patient:</span>
                      <span style={patientValueStyle}>{safe(prescription?.patient_snapshot?.full_name) || ""}</span>
                    </div>
                    <div style={patientRowStyle}>
                      <span style={patientLabelStyle}>Address:</span>
                      <span style={patientValueStyle}>{safe(prescription?.patient_snapshot?.address) || ""}</span>
                    </div>
                    <div style={{ ...patientRowStyle, gap: "10mm" }}>
                      <div style={{ display: "flex", alignItems: "baseline" }}>
                        <span style={{ ...patientLabelStyle, minWidth: "10mm" }}>Age:</span>
                        <span style={patientValueStyle}>{safe(prescription?.patient_snapshot?.age, "—")}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "baseline" }}>
                        <span style={{ ...patientLabelStyle, minWidth: "16mm" }}>Gender:</span>
                        <span style={patientValueStyle}>{safe(prescription?.patient_snapshot?.gender, "—")}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "baseline" }}>
                        <span style={{ ...patientLabelStyle, minWidth: "12mm" }}>Date:</span>
                        <span style={patientValueStyle}>{formatDate(prescription?.date)}</span>
                      </div>
                    </div>
                  </div>
                </td></tr>
              </thead>
              <tbody>
                <tr><td style={{ padding: "3mm 10mm 12mm 10mm", verticalAlign: "top", fontSize: `${bodyFontSize}pt` }}>

                  <div className="text-center mb-4 print-keep">
                    <div className="inline-block border-2 border-slate-900 px-6 py-1.5 uppercase font-bold tracking-[0.3em]" style={{ fontFamily: "Work Sans, Arial, sans-serif", fontSize: "14pt" }}>
                      Request
                    </div>
                  </div>

                  <div className="whitespace-pre-wrap text-slate-900 leading-relaxed" data-testid="rx-request-body" style={{ paddingLeft: "22mm" }}>
                    {prescription.request_text}
                  </div>

                  <div className="print-keep" style={{ marginTop: "16mm", display: "flex", justifyContent: "flex-end" }}>
                    <div className="text-left" style={{ width: "58mm", fontSize: "8pt" }}>
                      {(doctor.signature_image_url || s.signature_image_url) ? (
                        <img src={doctor.signature_image_url || s.signature_image_url} alt="signature" className="rx-signature" style={{ maxHeight: "14mm", marginBottom: "-6px" }} />
                      ) : (<div style={{ height: "10mm" }} />)}
                      <div className="font-bold uppercase leading-tight" style={{ fontSize: "8pt" }}>
                        {safe(prescription?.doctor_snapshot?.name, s.doctor_name) || "DOCTOR NAME, MD"}
                      </div>
                      <div className="text-[8pt] mt-0.5">
                        Lic. No. : <span className="underline">{safe(prescription?.doctor_snapshot?.license_number) || safe(s.license_number) || "_____________"}</span>
                      </div>
                      <div className="text-[8pt]">
                        PTR No. : <span className="underline">{safe(prescription?.doctor_snapshot?.ptr_number) || safe(s.ptr_number) || "_____________"}</span>
                      </div>
                      {prescription?.doctor_snapshot?.s2_number ? (
                        <div className="text-[8pt]">
                          S2 No. : <span className="underline">{prescription.doctor_snapshot.s2_number}</span>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="text-[7pt] text-slate-400 mt-4">
                    Rx# {safe(prescription?.prescription_number, "DRAFT")} · Request page
                  </div>

                </td></tr>
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
}
